import { Link } from "@tanstack/react-router";
import { formatDate } from "@/lib/site";
import { COVER_SIZES, coverSrcSet } from "@/lib/cover-image-url";

type Article = {
  id: string;
  slug: string;
  title: string;
  subtitle?: string | null;
  excerpt?: string | null;
  cover_image_url?: string | null;
  cover_image_alt?: string | null;
  category_id?: string | null;
  published_at?: string | null;
  reading_minutes?: number | null;
  author_name?: string | null;
};

type CategoryMap = Map<string, { slug: string; name: string }>;
type CoverContext = "feature" | "card" | "row";

function categoryFor(article: Article, categories: CategoryMap) {
  if (!article.category_id) return null;
  return categories.get(article.category_id) ?? null;
}

function CoverImage({
  article,
  priority = false,
  context = "card",
}: {
  article: Article;
  priority?: boolean;
  context?: CoverContext;
}) {
  if (article.cover_image_url) {
    const largest = context === "feature" ? 1280 : context === "row" ? 400 : 960;
    const sources = coverSrcSet(article.cover_image_url, {
      sizes: COVER_SIZES[context],
      largest,
    });
    return (
      <img
        src={sources?.src ?? article.cover_image_url}
        srcSet={sources?.srcSet}
        sizes={sources?.sizes}
        alt={article.cover_image_alt ?? article.title}
        className="aspect-[16/10] w-full object-cover"
        loading={priority ? "eager" : "lazy"}
        decoding="async"
        {...(priority ? { fetchPriority: "high" as const } : {})}
        width={1280}
        height={800}
      />
    );
  }
  return (
    <div className="aspect-[16/10] w-full bg-paper-soft border border-border flex items-center justify-center">
      <span className="headline-display text-6xl text-foreground/15">M.O.</span>
    </div>
  );
}

export function FeatureCard({
  article,
  categories,
}: {
  article: Article;
  categories: CategoryMap;
}) {
  const cat = categoryFor(article, categories);
  return (
    <article className="grid gap-6 md:grid-cols-2 md:gap-10">
      <Link
        to="/article/$slug"
        params={{ slug: article.slug }}
        className="block overflow-hidden"
      >
        <CoverImage article={article} priority context="feature" />
      </Link>
      <div className="flex flex-col justify-center">
        {cat && (
          <Link
            to="/category/$slug"
            params={{ slug: cat.slug }}
            className="eyebrow inline-block self-start py-1 hover:text-accent"
          >
            {cat.name}
          </Link>
        )}
        <Link to="/article/$slug" params={{ slug: article.slug }} className="mt-3 inline-block">
          <h2 className="headline-display text-3xl sm:text-5xl leading-[1.02] hover:text-accent-foreground transition-colors">
            {article.title}
          </h2>
        </Link>
        {article.subtitle && (
          <p className="mt-4 text-lg text-muted-foreground font-serif italic leading-snug">
            {article.subtitle}
          </p>
        )}
        {article.excerpt && (
          <p className="mt-4 text-base text-foreground/80 line-clamp-3">{article.excerpt}</p>
        )}
        <div className="mt-5 text-xs text-muted-foreground flex items-center gap-3">
          <span>{article.author_name ?? "Map Observer"}</span>
          <span aria-hidden>·</span>
          <span>{formatDate(article.published_at)}</span>
          {article.reading_minutes ? (
            <>
              <span aria-hidden>·</span>
              <span>{article.reading_minutes} min read</span>
            </>
          ) : null}
        </div>
      </div>
    </article>
  );
}

export function ArticleCard({
  article,
  categories,
  variant = "default",
}: {
  article: Article;
  categories: CategoryMap;
  variant?: "default" | "compact";
}) {
  const cat = categoryFor(article, categories);
  return (
    <article className="group flex flex-col">
      <Link to="/article/$slug" params={{ slug: article.slug }} className="block overflow-hidden">
        <CoverImage article={article} context="card" />

      </Link>
      <div className="mt-4 flex flex-col">
        {cat && (
          <Link
            to="/category/$slug"
            params={{ slug: cat.slug }}
            className="eyebrow inline-block self-start py-1 hover:text-accent"
          >
            {cat.name}
          </Link>
        )}
        <Link to="/article/$slug" params={{ slug: article.slug }} className="mt-2 inline-block">
          <h3
            className={
              variant === "compact"
                ? "headline-display text-xl leading-tight group-hover:text-accent-foreground"
                : "headline-display text-2xl leading-tight group-hover:text-accent-foreground"
            }
          >
            {article.title}
          </h3>
        </Link>
        {variant !== "compact" && article.excerpt && (
          <p className="mt-2 text-sm text-foreground/75 line-clamp-3">{article.excerpt}</p>
        )}
        <div className="mt-3 text-[11px] text-muted-foreground uppercase tracking-wider flex items-center gap-2">
          <span>{formatDate(article.published_at)}</span>
          {article.reading_minutes ? (
            <>
              <span aria-hidden>·</span>
              <span>{article.reading_minutes} min</span>
            </>
          ) : null}
        </div>
      </div>
    </article>
  );
}

export function ArticleRow({
  article,
  categories,
}: {
  article: Article;
  categories: CategoryMap;
}) {
  const cat = categoryFor(article, categories);
  return (
    <article className="border-t border-border py-6 flex gap-6">
      <div className="flex-1">
        {cat && (
          <Link
            to="/category/$slug"
            params={{ slug: cat.slug }}
            className="eyebrow inline-block self-start py-1 hover:text-accent"
          >
            {cat.name}
          </Link>
        )}
        <Link to="/article/$slug" params={{ slug: article.slug }} className="mt-2 inline-block">
          <h3 className="headline-display text-2xl leading-tight hover:text-accent-foreground">
            {article.title}
          </h3>
        </Link>
        {article.excerpt && (
          <p className="mt-2 text-sm text-foreground/75 line-clamp-2 max-w-2xl">
            {article.excerpt}
          </p>
        )}
        <div className="mt-2 text-[11px] text-muted-foreground uppercase tracking-wider">
          {formatDate(article.published_at)}
          {article.reading_minutes ? ` · ${article.reading_minutes} min` : ""}
        </div>
      </div>
      <Link
        to="/article/$slug"
        params={{ slug: article.slug }}
        className="hidden sm:block w-44 shrink-0"
      >
        <CoverImage article={article} context="row" />
      </Link>
    </article>
  );
}
